import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import SEOHead from "./SEOHead";
import config from "../config/config";
import { requestBrochureDownload } from "../utils/brochureDownload";

const HomePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);

  // Slider data
  const [sliderData, setSliderData] = useState([]);
  const [sliderLoading, setSliderLoading] = useState(true);

  const [brochureEmail, setBrochureEmail] = useState("");
  const [brochureError, setBrochureError] = useState("");
  const [brochureSubmitting, setBrochureSubmitting] = useState(false);

  // Fallback data functions
  const getFallbackServices = () => {
    return [
      {
        id: 1,
        title: "Manufacturing Solutions",
        description: "Comprehensive manufacturing services tailored to your needs",
        icon: "fas fa-cogs",
        image_url: config.getImageUrl("img_1.png")
      },
      {
        id: 2,
        title: "Quality Assurance",
        description: "Rigorous quality control and testing procedures",
        icon: "fas fa-check-circle",
        image_url: config.getImageUrl("img_2.png")
      },
      {
        id: 3,
        title: "Technical Support",
        description: "24/7 technical support and maintenance services",
        icon: "fas fa-headset",
        image_url: config.getImageUrl("img_3.png")
      },
      {
        id: 4,
        title: "Custom Solutions",
        description: "Bespoke solutions designed for your specific requirements",
        icon: "fas fa-tools",
        image_url: config.getImageUrl("img_1.png")
      },
      {
        id: 5,
        title: "Global Distribution",
        description: "Worldwide distribution network for efficient delivery",
        icon: "fas fa-globe",
        image_url: config.getImageUrl("img_2.png")
      }
    ];
  };

  const getFallbackProducts = () => {
    return [
      {
        id: 1,
        title: "Industrial Equipment",
        description: "High-quality industrial machinery and equipment",
        category: "Machinery",
        image_url: config.getImageUrl("img_1.png"),
        icon: "fas fa-industry"
      },
      {
        id: 2,
        title: "Automation Systems",
        description: "Advanced automation solutions for modern industries",
        category: "Automation",
        image_url: config.getImageUrl("img_2.png"),
        icon: "fas fa-robot"
      },
      {
        id: 3,
        title: "Quality Control Tools",
        description: "Precision instruments for quality assurance",
        category: "Tools",
        image_url: config.getImageUrl("img_3.png"),
        icon: "fas fa-microscope"
      }
    ];
  };

  const getFallbackSliderData = () => {
    return [
      {
        id: 1,
        backgroundImage: config.getImageUrl("bg_1.png"),
        title: "Welcome to NCG Corporate",
        highlight: "Leading Manufacturing Solutions",
        subtitle: "Innovation at its Best",
        description: "We provide cutting-edge manufacturing solutions and quality products for your business needs.",
        buttonText: "Learn More",
        buttonUrl: "/about-us",
        redSection1: {
          title: "SCM & Retail Network",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_1.png",
          url: "/services"
        },
        redSection2: {
          title: "Procurement Outsourcing Services",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_2.png",
          url: "/services"
        },
        redSection3: {
          title: "Engineering Design Services",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_3.png",
          url: "/services"
        }
      },
      {
        id: 2,
        backgroundImage: config.getImageUrl("about_bg.png"),
        title: "Quality Assurance",
        highlight: "Excellence in Every Product",
        subtitle: "Trusted by Industry Leaders",
        description: "Our commitment to quality ensures that every product meets the highest standards.",
        buttonText: "Our Services",
        buttonUrl: "/services",
        redSection1: {
          title: "SCM & Retail Network",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_1.png",
          url: "/services"
        },
        redSection2: {
          title: "Procurement Outsourcing Services",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_2.png",
          url: "/services"
        },
        redSection3: {
          title: "Engineering Design Services",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_3.png",
          url: "/services"
        }
      },
      {
        id: 3,
        backgroundImage: config.getImageUrl("bg_1.png"),
        title: "Global Reach",
        highlight: "Worldwide Distribution",
        subtitle: "Serving Customers Globally",
        description: "With our extensive network, we deliver solutions to customers around the world.",
        buttonText: "Contact Us",
        buttonUrl: "/contact-us",
        redSection1: {
          title: "SCM & Retail Network",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_1.png",
          url: "/services"
        },
        redSection2: {
          title: "Procurement Outsourcing Services",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_2.png",
          url: "/services"
        },
        redSection3: {
          title: "Engineering Design Services",
          description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          image: "img_3.png",
          url: "/services"
        }
      }
    ];
  };

  // Fetch services and products on component mount
  useEffect(() => {
    console.log("HomePage component mounted");
    // Use fallback data instead of API calls until API is deployed
    // Fetch data from database
    fetchServices();
    fetchProducts();
    fetchSliderData();
    
    // Use fallback data if API fails (handled in catch blocks of fetch functions)
  }, []);

  // Auto-play slider
  useEffect(() => {
    if (sliderData.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) =>
        prevSlide === sliderData.length - 1 ? 0 : prevSlide + 1
      );
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [sliderData.length]);

  // Slider functions
  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  const nextSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide === sliderData.length - 1 ? 0 : prevSlide + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide === 0 ? sliderData.length - 1 : prevSlide - 1
    );
  };

  // Handle slider button click
  const handleSliderButtonClick = (buttonUrl) => {
    if (!buttonUrl) return;
    
    // Check if it's an external URL
    if (buttonUrl.startsWith('http://') || buttonUrl.startsWith('https://')) {
      window.open(buttonUrl, '_blank');
    } else {
      // Internal navigation - use React Router
      if (buttonUrl.startsWith('/')) {
        navigate(buttonUrl);
      } else {
        // Relative path - navigate to it
        navigate(`/${buttonUrl}`);
      }
    }
  };

  const fetchServices = async () => {
    try {
              const baseApiUrl = config.getApiUrl("");

      const response = await fetch(`${baseApiUrl}get_services.php`);
      const data = await response.json();

      if (data.success) {
        // Only take the top 5 services for homepage display
        const topServices = data.services ? data.services.slice(0, 5) : [];
        setServices(topServices);
      } else {
        console.error("Failed to fetch services:", data.message);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      // Use fallback data if API fails
      setServices(getFallbackServices());
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
              const baseApiUrl = config.getApiUrl("");

      const response = await fetch(`${baseApiUrl}get_products.php`);
      const data = await response.json();

      if (data.success) {
        setProducts(data.data);
      } else {
        console.error("Failed to fetch products:", data.message);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      // Use fallback data if API fails
      setProducts(getFallbackProducts());
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchSliderData = async () => {
    try {
      setSliderLoading(true);
              const baseApiUrl = config.getApiUrl("");

      const response = await fetch(`${baseApiUrl}get_slider_items.php`);
      const data = await response.json();

      if (data.success) {
        // Transform the data to match the expected format
        const transformedData = data.slider_items.map((item) => ({
          id: item.id,
          backgroundImage: config.getImageUrl(item.background_image),
          title: item.title,
          highlight: item.highlight,
          subtitle: item.subtitle,
          description: item.description,
          buttonText: item.button_text,
          buttonUrl: item.button_url || "",
          // Add red section data
          redSection1: {
            title: item.red_section_1_title || "SCM & Retail Network",
            description: item.red_section_1_description || "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
            image: item.red_section_1_image || "img_1.png",
            url: item.red_section_1_url || ""
          },
          redSection2: {
            title: item.red_section_2_title || "Procurement Outsourcing Services",
            description: item.red_section_2_description || "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
            image: item.red_section_2_image || "img_2.png",
            url: item.red_section_2_url || ""
          },
          redSection3: {
            title: item.red_section_3_title || "Engineering Design Services",
            description: item.red_section_3_description || "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
            image: item.red_section_3_image || "img_3.png",
            url: item.red_section_3_url || ""
          }
        }));

        setSliderData(transformedData);
      } else {
        console.error("Failed to fetch slider data:", data.message);
      }
    } catch (error) {
      console.error("Error fetching slider data:", error);
    } finally {
      setSliderLoading(false);
    }
  };

  return (
    <>
      <SEOHead pageType="homepage" />

      {/* Hero Slider Section */}
      <section
        className="hero-slider"
        style={{ minHeight: "544px", background: "#1e3a5f" }}
      >
        <div className="slider-container">
          {sliderLoading ? (
            <div className="slider-slide active">
              <div className="slider-overlay">
                <div className="container">
                  <div className="row align-items-center">
                    <div className="col-12 text-center">
                      <div className="spinner-border text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-light mt-3">
                        Loading slider content...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : sliderData.length > 0 ? (
            <>
              {/* Debug: Show all slides for testing */}
              {sliderData.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`slider-slide ${
                    index === currentSlide ? "active" : ""
                  }`}
                  style={{
                    backgroundImage: `url(${slide.backgroundImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <div className="slider-overlay">
                    <div className="container">
                      <div className="row align-items-center">
                        {/* Left side: Main content */}
                        <div className="col-lg-7">
                          <div className="hero-content">
                            <h1 className="hero-main-title">
                              {slide.title}{" "}
                              <span className="hero-highlight">
                                {slide.highlight}
                              </span>
                              <br />
                              {slide.subtitle}
                            </h1>
                            <p className="hero-description">
                              {slide.description}
                            </p>
                            {slide.buttonUrl ? (
                              <button 
                                className="hero-know-more-btn"
                                onClick={() => handleSliderButtonClick(slide.buttonUrl)}
                                style={{ cursor: 'pointer' }}
                              >
                                {slide.buttonText}{" "}
                                <i className="fas fa-arrow-right ms-2"></i>
                              </button>
                            ) : (
                              <button className="hero-know-more-btn" disabled style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                {slide.buttonText}{" "}
                                <i className="fas fa-arrow-right ms-2"></i>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Right side: Service cards */}
                        <div className="col-lg-5">
                          <div className="hero-services">
                            <div 
                              className="hero-service-box" 
                              style={{ cursor: slide.redSection1.url ? 'pointer' : 'default' }}
                              onClick={() => slide.redSection1.url && handleSliderButtonClick(slide.redSection1.url)}
                            >
                              <div className="service-content">
                                <h6 className="service-title">
                                  {slide.redSection1.title}
                                </h6>
                                <p className="service-description">
                                  {slide.redSection1.description.length > 30 
                                    ? slide.redSection1.description.substring(0, 30) + '...' 
                                    : slide.redSection1.description}
                                </p>
                              </div>
                              <div className="service-image">
                                <img
                                  src={config.getImageUrl(slide.redSection1.image)}
                                  alt={slide.redSection1.title}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              </div>
                            </div>

                            <div 
                              className="hero-service-box" 
                              style={{ cursor: slide.redSection2.url ? 'pointer' : 'default' }}
                              onClick={() => slide.redSection2.url && handleSliderButtonClick(slide.redSection2.url)}
                            >
                              <div className="service-content">
                                <h6 className="service-title">
                                  {slide.redSection2.title}
                                </h6>
                                <p className="service-description">
                                  {slide.redSection2.description.length > 30 
                                    ? slide.redSection2.description.substring(0, 30) + '...' 
                                    : slide.redSection2.description}
                                </p>
                              </div>
                              <div className="service-image">
                                <img
                                  src={config.getImageUrl(slide.redSection2.image)}
                                  alt={slide.redSection2.title}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              </div>
                            </div>

                            <div 
                              className="hero-service-box" 
                              style={{ cursor: slide.redSection3.url ? 'pointer' : 'default' }}
                              onClick={() => slide.redSection3.url && handleSliderButtonClick(slide.redSection3.url)}
                            >
                              <div className="service-content">
                                <h6 className="service-title">
                                  {slide.redSection3.title}
                                </h6>
                                <p className="service-description">
                                  {slide.redSection3.description.length > 30 
                                    ? slide.redSection3.description.substring(0, 30) + '...' 
                                    : slide.redSection3.description}
                                </p>
                              </div>
                              <div className="service-image">
                                <img
                                  src={config.getImageUrl(slide.redSection3.image)}
                                  alt={slide.redSection3.title}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div
              className="slider-slide active"
              style={{
                background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a8b 100%)",
              }}
            >
              <div className="slider-overlay">
                <div className="container">
                  <div className="row align-items-center">
                    <div className="col-12 text-center">
                      <div className="hero-content">
                        <h1
                          className="hero-main-title"
                          style={{
                            color: "white",
                            fontSize: "48px",
                            fontWeight: "bold",
                          }}
                        >
                          Welcome to{" "}
                          <span style={{ color: "#f6c30d" }}>
                            NCG Corporate
                          </span>
                          <br />
                          Manufacturing Excellence
                        </h1>
                        <p
                          className="hero-description"
                          style={{ color: "white", fontSize: "18px" }}
                        >
                          Leading procurement and engineering design services
                          for global clients
                        </p>
                        <button
                          className="hero-know-more-btn"
                          style={{
                            background: "#f6c30d",
                            color: "#1e3a5f",
                            border: "none",
                            padding: "12px 25px",
                            borderRadius: "25px",
                            fontWeight: "bold",
                          }}
                        >
                          Know More <i className="fas fa-arrow-right ms-2"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Slider Navigation */}
        {sliderData.length > 0 && (
          <>
            <div className="slider-navigation">
              <button className="slider-nav-btn prev" onClick={prevSlide}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <button className="slider-nav-btn next" onClick={nextSlide}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>

            {/* Slider Indicators */}
            <div className="slider-indicators">
              {sliderData.map((slide, index) => (
                <button
                  key={slide.id}
                  className={`slider-indicator ${
                    index === currentSlide ? "active" : ""
                  }`}
                  onClick={() => goToSlide(index)}
                >
                  <span className="indicator-dot"></span>
                </button>
              ))}
            </div>

            {/* Slide Counter */}
            <div className="slider-counter">
              {currentSlide + 1} / {sliderData.length}
            </div>
          </>
        )}
      </section>

      {/* About Section */}
      <section className="about-section" id="about">
        <div className="container">
          <div className="row align-items-center">
            {/* Left side: White card with content */}
            <div className="col-lg-6">
              <div className="about-white-card">
                <h2 className="about-title">About Us</h2>
                <p className="about-paragraph">
                  NCG Corporate is a manufacturer and exporter of customized
                  engineering steel products. NCG management has more than 30+
                  years of years of experience in quality manufacturing,
                  sourcing, procurement and supply chain. The director is a
                  Quality certified auditor and Six Sigma certified. The company
                  aims to provide complete solutions and a seamless supply chain
                  for its clients with deep knowledge of sourcing, production,
                  design and manufacturing. The experience of the team augmented
                  with vast industry experience enhances the final value
                  delivered to its customers.
                </p>
                <a href="https://www.ncgcorporate.com/about-us"><button className="about-know-more-btn">
                  Know More <i className="fas fa-arrow-right ms-2"></i>
                </button></a>
              </div>
            </div>

            {/* Right side: About us image */}
            <div className="col-lg-6">
              <div className="about-image-placeholder">
                <img
                  src={config.getImageUrl("abou_img.png")}
                  alt="About NCG Corporate - Manufacturing Excellence"
                  className="about-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="products-section" id="products">
        <div className="container">
          {/* Products header with title and red line */}
          <div className="row">
            <div className="col-12">
              <div className="products-header">
                <h2 className="products-main-title">Our Products</h2>
                <div className="products-red-line"></div>
              </div>
            </div>
          </div>

          {productsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Layout: 3 products per row */}
              <div className="d-none d-lg-block">
                {/* First row of 3 products */}
                <div className="row">
                  {products.slice(0, 3).map((product, index) => (
                    <div key={product.id} className="col-lg-4">
                      <div
                        className={`product-card ${
                          index === 0 ? "featured-card" : ""
                        }`}
                      >
                        <div className="product-icon">
                          <i className={product.icon || "fas fa-box"}></i>
                        </div>
                        <h5 className="product-card-title">{product.title}</h5>
                        <div className="product-divider"></div>
                        <p className="product-card-description">
                          {product.description}
                        </p>
                        {product.link && (
                          <Link
                            to={product.link}
                            className="product-read-more"
                          >
                            Read More
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Second row of 3 products */}
                <div className="row mt-4">
                  {products.slice(3, 6).map((product) => (
                    <div key={product.id} className="col-lg-4">
                      <div className="product-card">
                        <div className="product-icon">
                          <i className={product.icon || "fas fa-box"}></i>
                        </div>
                        <h5 className="product-card-title">{product.title}</h5>
                        <div className="product-divider"></div>
                        <p className="product-card-description">
                          {product.description}
                        </p>
                        {product.link && (
                          <Link
                            to={product.link}
                            className="product-read-more"
                          >
                            Read More
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Third row of 3 products */}
                <div className="row mt-4">
                  {products.slice(6, 9).map((product) => (
                    <div key={product.id} className="col-lg-4">
                      <div className="product-card">
                        <div className="product-icon">
                          <i className={product.icon || "fas fa-box"}></i>
                        </div>
                        <h5 className="product-card-title">{product.title}</h5>
                        <div className="product-divider"></div>
                        <p className="product-card-description">
                          {product.description}
                        </p>
                        {product.link && (
                          <Link
                            to={product.link}
                            className="product-read-more"
                          >
                            Read More
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile Layout: 2 products per row */}
              <div className="d-lg-none">
                {/* Group products into pairs for mobile */}
                {Array.from({ length: Math.ceil(products.length / 2) }, (_, rowIndex) => (
                  <div key={rowIndex} className="row mb-3">
                    {products.slice(rowIndex * 2, rowIndex * 2 + 2).map((product, index) => (
                      <div key={product.id} className="col-6">
                        <div
                          className={`product-card ${
                            rowIndex === 0 && index === 0 ? "featured-card" : ""
                          }`}
                        >
                          <div className="product-icon">
                            <i className={product.icon || "fas fa-box"}></i>
                          </div>
                          <h5 className="product-card-title">{product.title}</h5>
                          <div className="product-divider"></div>
                          <p className="product-card-description">
                            {product.description}
                          </p>
                          {product.link && (
                            <a
                              href={product.link}
                              className="product-read-more"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Read More
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Red CTA Section */}
      <section
        className="cta-red-section"
        style={{
          backgroundImage: `url(${config.getImageUrl("download_bro.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Transparent red overlay on right half */}
        <div className="cta-overlay"></div>

        {/* Form content positioned over the right side */}
        <div className="cta-content-area">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBrochureError("");
              setBrochureSubmitting(true);
              const result = await requestBrochureDownload(brochureEmail);
              setBrochureSubmitting(false);
              if (!result.ok) {
                setBrochureError(result.error);
                return;
              }
              setBrochureEmail("");
            }}
            noValidate
          >
            <h3 className="cta-title-new">Download Our Brochure</h3>

            <label htmlFor="brochure-email-home" className="cta-form-label-new">
              Email Address
            </label>
            <input
              type="email"
              id="brochure-email-home"
              name="brochure-email"
              className="cta-email-input-new"
              placeholder="Email Address*"
              value={brochureEmail}
              onChange={(e) => setBrochureEmail(e.target.value)}
              autoComplete="email"
            />
            <button
              type="submit"
              className="cta-know-more-btn-new"
              disabled={brochureSubmitting}
            >
              Know More <i className="fas fa-arrow-right ms-2"></i>
            </button>
            {brochureError ? (
              <p className="cta-brochure-error" role="alert">
                {brochureError}
              </p>
            ) : null}
          </form>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="services">
        <div className="services-wrapper">
          {/* Full-width black background */}
          <div
            className="black-full-bg"
            style={{
              backgroundImage: `url(${config.getImageUrl("download_link.png")})`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
            }}
          ></div>

          <div className="container">
            {/* Services header with title and red line */}
            <div className="row">
              <div className="col-12">
                <div className="services-header">
                  <h2 className="services-main-title">Our Services</h2>
                  <div className="services-red-line"></div>
                </div>
              </div>
            </div>

            {/* Services description */}
            <div className="services-intro-area">
              <p className="services-description">
                NCG is an end to end Procurement & Engineering Design service
                company focused on providing high quality service to our global
                clients. NCG team has more than 30+ years of hands on experience
                in procurement, supply chain, engineering and manufacturing.
              </p>
            </div>

            {/* Service cards grid */}
            <div className="services-container">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="services-row">
                  {services.map((service) => (
                    <div key={service.id} className="service-card">
                      <div className="service-small-red-icon">
                        {service.icon && <i className={service.icon}></i>}
                      </div>
                      <h6 className="service-card-title">{service.title}</h6>
                      <p className="service-card-text">{service.description}</p>
                      {service.link_url ? (
                        <a
                          href={service.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="service-read-more-btn"
                        >
                          Read More
                        </a>
                      ) : (
                        <button
                          className="service-read-more-btn"
                          onClick={() => {
                            // You can add navigation to a services page or show more details
                            alert(`Learn more about ${service.title}`);
                          }}
                        >
                          Read More
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Explore More button */}
            <div className="row">
              <div className="col-12 text-center">
                <button className="services-explore-btn">
                  Explore More <i className="fas fa-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Information Section */}
      <section className="additional-info-section" id="contact">
        <div className="container">
          <div className="row">
            {/* Column 1: Spotlight */}
            <div className="col-lg-4 col-md-6">
              <div className="info-column">
                <h6>Spotlight</h6>
                <h5>Exports of Fencing Accessories</h5>
                <ul className="spotlight-copy-list">
                  <li>
                    Exports of premium fencing accessories, crafted with
                    precision and strength.
                  </li>
                  <li>
                    Engineered for durability, ensuring long-lasting performance
                    in every environment.
                  </li>
                  <li>
                    Manufactured with high-quality materials, meeting global
                    standards and demands.
                  </li>
                  <li>
                    Trusted worldwide for reliability, innovation, and superior
                    finishing.
                  </li>
                </ul>
              </div>
            </div>

            {/* Column 2: Address */}
            <div className="col-lg-4 col-md-6">
              <div className="info-column">
                <h5>Address</h5>
                <address>
                  <strong>Registered Office:</strong>
                  <br />
                  SCO: 117/118(2nd Floor), Sector -17/B,
                  <br />
                  Chandigarh, India, Pin- 160017
                  <br />
                  <strong>Phone:</strong>
                  <a href="tel:01724014825">0172-4014825</a>
                  <br />
                  <strong>Cell no:</strong>
                  <a href="tel:+919779044006">+91-9779044006</a>
                  <br />
                  <strong>Email:</strong>
                  <a href="mailto:info@ncgcorporate.com">
                    info@ncgcorporate.com
                  </a>
                </address>
              </div>
            </div>

            {/* Column 3: Factory/Corporate Office */}
            <div className="col-lg-4 col-md-6">
              <div className="info-column">
                <h5>Factory/Corporate Office:</h5>
                <address>
                  <strong>Plot No.</strong> C-134, Industrial Area Phase VIII,
                  <br />
                  Sector 72, SAS Nagar Mohali, Punjab,
                  <br />
                  India, Pin- 160071
                  <br />
                  <strong>Cell:</strong>
                  <a href="tel:+919877078268">+91-9877078268</a> /
                  <a href="tel:+919779044006">+91-9779044006</a>
                </address>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
